import type { ReactNode } from "react";
import { ArrowRight, Clock, DollarSign, Package, Truck } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { ToolCtaLink } from "@/components/calculators/shared/tool-cta-link";
import { formatCents } from "@/lib/formatting/currency";
import type { PalletShippingMode } from "@shared/freight/shipping-mode";
import { deliveredQuoteHref } from "./freight-quote-request";
import type { FreightQuote } from "./use-freight-quote";

const MODE_LABELS: Record<PalletShippingMode, string> = {
  LTL: "LTL",
  FTL: "Full truckload",
};

function QuoteFact({ icon, term, value }: { icon: ReactNode; term: string; value: string }) {
  return (
    <div className="flex items-center gap-2 border border-border p-3">
      {icon}
      <div>
        <dt className="text-xs text-muted-foreground">{term}</dt>
        <dd className="text-sm font-medium text-foreground">{value}</dd>
      </div>
    </div>
  );
}

const ICON_CLASS = "h-4 w-4 text-muted-foreground";

function pluralDays(days: number): string {
  return `${days} day${days === 1 ? "" : "s"}`;
}

export function FreightQuoteResult({ quote }: { quote: FreightQuote }) {
  return (
    <div className="mt-4 border border-border bg-card">
      <div className="border-b border-border bg-muted px-6 py-3">
        <span className="text-xs font-medium text-muted-foreground">Quote Result</span>
      </div>
      <div className="p-6">
        <div className="mb-4 text-center">
          <p className="text-3xl font-bold text-foreground">{formatCents(quote.displayPriceCents)}</p>
          <p className="text-xs text-muted-foreground">Estimated freight cost</p>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2">
          <QuoteFact icon={<Package aria-hidden className={ICON_CLASS} />} term="Shipping Mode" value={MODE_LABELS[quote.shippingMode]} />
          <QuoteFact icon={<DollarSign aria-hidden className={ICON_CLASS} />} term="Per Pallet" value={formatCents(Math.round(quote.displayPriceCents / quote.palletCount))} />
          {quote.transitDays !== null ? (
            <QuoteFact icon={<Clock aria-hidden className={ICON_CLASS} />} term="Transit Time" value={pluralDays(quote.transitDays)} />
          ) : null}
          {quote.carrier ? (
            <QuoteFact icon={<Truck aria-hidden className={ICON_CLASS} />} term="Carrier" value={quote.carrier} />
          ) : null}
        </dl>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Quotes are estimates and subject to change. Final pricing confirmed at booking.
        </p>
        <Button variant="primary" asChild className="mt-4 w-full">
          <ToolCtaLink tool="freight-calculator" href={deliveredQuoteHref(quote)}>
            Request a delivered quote
            <ArrowRight aria-hidden />
          </ToolCtaLink>
        </Button>
      </div>
    </div>
  );
}
